import * as React from "react";
import { useCallback, useState, useMemo } from "react";
import { useFormikContext } from "formik";
import * as Immutable from "immutable";
import { styled } from "styled-components";
import { useTranslation } from "react-i18next";
import { Button } from "components/bootstrap";
import SearchBar from "data-lake/preview/storage-types/internal/SearchBar";
import DataLakePreview from "data-lake/preview/DataLakePreview";
import useInitialSearchBarFormValues from "data-lake/preview/storage-types/external/hooks/useInitialFormValues";
import { WIDGET_DEFAULTS, MAX_LOADED_MESSAGES, EXTERNAL_DATA_LAKE_SEARCH_TYPE } from "data-lake/preview/Constants";
import { onInitializingTimerange } from "views/components/TimerangeForForm";
import useUserDateTime from "hooks/useUserDateTime";
import { normalizeFromSearchBarForBackend } from "views/logic/queries/NormalizeTimeRange";
import Query from "views/logic/queries/Query";
import generateId from "logic/generateId";
import { EXTERNAL_DATA_LAKE_QUERY } from "data-lake/preview/storage-types/external/Constants";
import TableFilter from "data-lake/preview/storage-types/external/TableFilter";
import useSelectedDataLake from "data-lake/hooks/useSelectedDataLake";
import WidgetConfigurationProvider from "data-lake/preview/storage-types/external/WidgetConfigurationProvider";
import DataLakeConnectorCreateInputModal, {
  ExternalDataLakeInputType
} from "data-lake/data-lake-connectors/DataLakeConnectorCreateInputModal";
import RetrievalProvider from "data-lake/preview/storage-types/external/contexts/RetrievalProvider";
import TableList from "data-lake/preview/storage-types/external/TableList";
import { Icon, IfPermitted } from "components/common";
import useDataLakeTableOptions from "data-lake/preview/storage-types/external/hooks/useDataLakeTableOptions";
import useFieldTypes from "data-lake/hooks/useFieldTypes";
import Store from "logic/local-storage/Store";
import PreviewLayoutProvider from "./contexts/PreviewLayoutProvider";
const HIDE_DATA_LAKE_PREVIEW_TABLE_LIST = "hide_data_lake_preview_table_list";
const RightColContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;
  width: 100%;
`;
const StyledButton = styled(Button)`
  margin-right: 5px;
`;
const validate = (formValues) => {
  const errors = {};
  if (!formValues.table) {
    return { ...errors, table: "Table is required" };
  }
  return errors;
};
const fieldFiltersArrayToString = (fieldFiltersArray) => {
  if (!Array.isArray(fieldFiltersArray)) return "";
  return fieldFiltersArray.map((item) => `${item.field_name}=${item.value}`).join(",");
};
const CreateInputActionButton = ({ dataLakeId }) => {
  var _a, _b;
  const { t } = useTranslation("enterprise_data_lake");
  const { values } = useFormikContext();
  const [showCreateInputModal, setShowCreateInputModal] = useState(false);
  const toggleShowCreateInputModal = () => setShowCreateInputModal((cur) => !cur);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    StyledButton,
    {
      onClick: toggleShowCreateInputModal,
      title: t("external_preview.create_input_title"),
      bsStyle: "primary"
    },
    t("external_preview.create_input")
  ), showCreateInputModal && /* @__PURE__ */ React.createElement(
    DataLakeConnectorCreateInputModal,
    {
      connectorId: dataLakeId,
      tableId: values == null ? void 0 : values.table,
      filterQuery: fieldFiltersArrayToString((_a = values == null ? void 0 : values.fields) == null ? void 0 : _a.fieldFilters),
      filterQueryOperator: (_b = values == null ? void 0 : values.fields) == null ? void 0 : _b.operator,
      onClose: toggleShowCreateInputModal
    }
  ));
};
const ExternalDataLakeSearchBar = ({
  executedSearch,
  onReset,
  onSubmit,
  formRef = void 0,
  searchJobIsRunning,
  initialUrlParams,
  toggleTableList,
  showTableList
}) => {
  const { t } = useTranslation("enterprise_data_lake");
  const initialFormValues = useInitialSearchBarFormValues(executedSearch, initialUrlParams);
  const { setDataLakeFieldsFetchQuery, selectedDataLake } = useSelectedDataLake();
  const { isLoading: isLoadingFields } = useFieldTypes();
  const _onReset = useCallback(() => {
    setDataLakeFieldsFetchQuery(null);
    return onReset();
  }, [onReset, setDataLakeFieldsFetchQuery]);
  const { data: tables, isLoading: isLoadingTables } = useDataLakeTableOptions(selectedDataLake == null ? void 0 : selectedDataLake.id);
  const hasOnlyOneCatalog = useMemo(
    () => (tables == null ? void 0 : tables.length) > 0 && tables.every((item) => item.catalog === tables[0].catalog && item.schema === tables[0].schema),
    [tables]
  );
  return /* @__PURE__ */ React.createElement(
    SearchBar,
    {
      rightColumn: /* @__PURE__ */ React.createElement(RightColContainer, null, /* @__PURE__ */ React.createElement(TableFilter, { tables, isLoading: isLoadingTables, hasOnlyOneCatalog }), /* @__PURE__ */ React.createElement(Button, { onClick: toggleTableList }, /* @__PURE__ */ React.createElement(Icon, { name: "list" }), showTableList ? t("external_preview.hide_table_list") : t("external_preview.show_table_list"))),
      actions: /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`inputs:create:${ExternalDataLakeInputType}`] }, /* @__PURE__ */ React.createElement(CreateInputActionButton, { dataLakeId: selectedDataLake == null ? void 0 : selectedDataLake.id })),
      validate,
      emptyFormValues: {
        timerange: WIDGET_DEFAULTS.timerange,
        table: void 0,
        fields: void 0
      },
      disableSubmit: isLoadingTables || isLoadingFields,
      initialValues: initialFormValues,
      onReset: _onReset,
      onSubmit,
      formRef,
      searchJobIsRunning
    }
  );
};
const ExternalDataLakePreview = ({ initialSearch, initialSearchJob, initialUrlParams, resetUrlQueryParams }) => {
  const { formatTime, userTimezone } = useUserDateTime();
  const { selectedDataLake } = useSelectedDataLake();
  const [showTableList, setShowTableList] = useState(Store.get(HIDE_DATA_LAKE_PREVIEW_TABLE_LIST) !== "true");
  const toggleTableList = useCallback(() => {
    setShowTableList((cur) => {
      Store.set(HIDE_DATA_LAKE_PREVIEW_TABLE_LIST, cur ? "true" : "false");
      return !cur;
    });
  }, []);
  const renderSearchBar = useCallback(
    ({
      executedSearch,
      onReset,
      onSubmit,
      searchJobIsRunning,
      formRef
    }) => /* @__PURE__ */ React.createElement(
      ExternalDataLakeSearchBar,
      {
        showTableList,
        formRef,
        executedSearch,
        toggleTableList,
        onReset,
        onSubmit,
        searchJobIsRunning,
        initialUrlParams
      }
    ),
    [initialUrlParams, showTableList, toggleTableList]
  );
  const widgetFromFormValues = useCallback(
    (widget, { fields, timerange, table }) => {
      var _a;
      const updatedWidgetConfig = widget.config.toBuilder().fieldFilters((fields == null ? void 0 : fields.fieldFilters) ? Immutable.List(fields.fieldFilters) : widget.config.fieldFilters).filtersOperator((_a = fields == null ? void 0 : fields.operator) != null ? _a : widget.config.filtersOperator).build();
      return widget.toBuilder().config(updatedWidgetConfig).timerange(timerange ? normalizeFromSearchBarForBackend(timerange, userTimezone) : widget.timerange).streams([table]).build();
    },
    [userTimezone]
  );
  const filtersFromSearchType = useCallback(
    (searchType) => {
      var _a, _b;
      return {
        table: searchType.table_id,
        timerange: onInitializingTimerange(searchType.timerange, formatTime),
        fields: {
          fieldFilters: (_a = searchType.field_filters) != null ? _a : WIDGET_DEFAULTS.fieldFilters,
          operator: (_b = searchType.filters_operator) != null ? _b : WIDGET_DEFAULTS.fieldsOperator
        }
      };
    },
    [formatTime]
  );
  const queryFromFormValues = useCallback(
    (widget, { table }) => Query.builder().id(generateId()).query(EXTERNAL_DATA_LAKE_QUERY).searchTypes([
      {
        size: MAX_LOADED_MESSAGES,
        filters_operator: widget.config.filtersOperator,
        timerange: widget.timerange,
        table_id: table,
        data_lake_id: selectedDataLake == null ? void 0 : selectedDataLake.id,
        field_filters: widget.config.fieldFilters.toArray(),
        fields: widget.config.fields.toArray(),
        type: EXTERNAL_DATA_LAKE_SEARCH_TYPE,
        filters: []
      }
    ]).build(),
    [selectedDataLake == null ? void 0 : selectedDataLake.id]
  );
  const renderTableList = useCallback(
    (searchBarFormRef) => /* @__PURE__ */ React.createElement(TableList, { searchBarFormRef, onClose: toggleTableList }),
    [toggleTableList]
  );
  return /* @__PURE__ */ React.createElement(WidgetConfigurationProvider, { initialSearch }, /* @__PURE__ */ React.createElement(RetrievalProvider, null, /* @__PURE__ */ React.createElement(PreviewLayoutProvider, null, /* @__PURE__ */ React.createElement(
    DataLakePreview,
    {
      filtersFromSearchType,
      initialSearch,
      initialSearchJob,
      queryFromFormValues,
      resetUrlQueryParams,
      searchBar: renderSearchBar,
      leftCol: showTableList ? renderTableList : void 0,
      widgetFromFormValues
    }
  ))));
};
export default ExternalDataLakePreview;
